import { LitElement, html } from 'lit';
import '@vitesse/base-ui';
import { customElement, property } from 'lit/decorators.js';
import type { BackendClient } from '../../bridge/backend-client';
import type { ControllerLog } from '../../../core/bridge/connect-client';
import { type LogLine } from '../log-tree';
import '../log-tree';

/*
 * <vitesse-integration-logs> — the integration Logs tab. Thin wrapper that points
 * the shared <vitesse-log-tree> at this integration's stream
 * (Integrations.SubscribeLogs: recent-window replay + live tail) and maps each
 * line to the uniform LogLine. Mirrors <vitesse-controller-logs>. The detail page
 * mounts it EAGERLY (hidden until the Logs tab opens) so the recent-window replay
 * runs once on page open; the page also owns the access gate — it wraps this
 * section in <vitesse-gate-overlay .rpc=SubscribeLogs>, so no inner <vitesse-gated>
 * here. The payloads are producer-sanitized (the integrations service's natslog
 * handler) — black-box-safe status/lifecycle/errors.
 */
@customElement('vitesse-integration-logs')
export class IntegrationLogsSection extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() integrationId = '';

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM → shared `.lt-*` styles apply
  }

  /** IDENTICAL mapping to <vitesse-controller-logs> (verbatim): message → title,
   *  body the expanded detail; when the producer emits no `body`, synthesize one
   *  from the attrs via fmtAttr, dropping `addr`/`ok`. So the integration Logs tab
   *  is pixel- AND text-identical to the controller/model Logs tab. */
  private static toLine(item: unknown): LogLine {
    const l = item as ControllerLog;
    let body = l.body || undefined;
    if (!body) {
      const parts: string[] = [];
      for (const [k, v] of Object.entries(l.attrs)) {
        if (k === 'addr' || k === 'ok') continue;
        const [label, value] = fmtAttr(k, v);
        parts.push(`${label} ${value}`);
      }
      body = parts.length ? parts.join(' · ') : undefined;
    }
    return { tsMs: l.tsMs, severity: l.severity, title: l.message, body };
  }

  // STABLE connect (a fresh arrow each render would re-subscribe the whole tree).
  private readonly connectLogs = (): Promise<MessagePort> =>
    this.backend!.api.subscribeIntegrationLogs(this.gatewayId, this.integrationId);

  render() {
    const ready = !!(this.backend && this.integrationId);
    return html`<vitesse-log-tree
      .subscriptionKey=${ready ? this.integrationId : ''}
      .connect=${ready ? this.connectLogs : undefined}
      .toLine=${IntegrationLogsSection.toLine}
    ></vitesse-log-tree>`;
  }
}

/** key=value → a readable {label, value} — verbatim from <vitesse-controller-logs>
 *  so the two render attrs identically (rtt_ms=9 → rtt · 9ms; etc.). */
function fmtAttr(k: string, v: string): [label: string, value: string] {
  const units: [string, string][] = [
    ['_per_sec', '/s'],
    ['_ms', 'ms'],
    ['_pct', '%'],
    ['_s', 's'],
  ];
  for (const [suf, unit] of units) {
    if (k.endsWith(suf)) return [k.slice(0, -suf.length).replace(/_/g, ' '), v + unit];
  }
  return [k.replace(/_/g, ' '), v];
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-integration-logs': IntegrationLogsSection;
  }
}
